import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import PageHero from "../components/site/PageHero";
import { IMAGES } from "../lib/images";
import { useReveal } from "../lib/useReveal";
import { GraduationCap, Sprout, HandHeart, ArrowRight } from "lucide-react";

const AREAS = [
  {
    icon: HandHeart,
    title: "Humanitarian Assistance",
    text: "Responding to hardship with practical support — essential goods, services and direct assistance for individuals and families facing poverty.",
    image: IMAGES.volunteersAid,
  },
  {
    icon: GraduationCap,
    title: "Education & Learning",
    text: "Supporting access to learning, because education is one of the surest paths out of poverty for children and communities alike.",
    image: IMAGES.classroom,
  },
  {
    icon: Sprout,
    title: "Community Agriculture",
    text: "Encouraging sustainable, community-led agriculture so families can build food security and resilient livelihoods.",
    image: IMAGES.agriculture,
  },
];

export default function Work() {
  const ref = useReveal();
  return (
    <div ref={ref}>
      <Seo
        title="Our Work"
        path="/our-work"
        description="How Mount Moriah Relief works: humanitarian assistance, education support and community agriculture, delivered through responsible partnerships in Northern Ireland and Zimbabwe."
      />
      <PageHero
        eyebrow="Our Work"
        title="Practical help, responsibly delivered"
        image={IMAGES.volunteers.src}
        imageAlt={IMAGES.volunteers.alt}
      >
        <p>
          We prevent and relieve poverty, hardship and suffering through humanitarian
          assistance, grants, essential goods, services and collaboration with other
          organisations.
        </p>
      </PageHero>

      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-20">
          {AREAS.map((area, i) => (
            <article key={area.title} className={`grid lg:grid-cols-12 gap-10 items-center reveal`}>
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <img src={area.image.src} alt={area.image.alt} loading="lazy"
                  className="w-full aspect-[4/3] object-cover border border-sandstone" />
                <p className="mt-2 text-xs text-charcoal/50 italic">Illustrative photograph</p>
              </div>
              <div className={`lg:col-span-7 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <area.icon size={28} strokeWidth={1.5} className="text-gold" aria-hidden="true" />
                <h2 className="mt-5 font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight">{area.title}</h2>
                <p className="mt-4 text-charcoal/80 leading-relaxed max-w-xl">{area.text}</p>
                <p className="mt-4 text-sm text-charcoal/60 leading-relaxed max-w-xl">
                  This is an area of charitable intent we are developing with partners. We will
                  publish verified project updates in our <Link to="/news" className="text-forest underline">news section</Link> as
                  work is confirmed — we do not present plans as completed work.
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="py-24 bg-sandstone/50">
        <div className="max-w-3xl mx-auto px-6 text-center reveal">
          <h2 className="font-serif text-3xl sm:text-4xl font-medium text-forest tracking-tight leading-snug">
            Partnership is how we work
          </h2>
          <p className="mt-5 text-charcoal/80 leading-relaxed">
            We collaborate with established organisations and local partners so assistance
            reaches the right people, in the right way. If your organisation shares our mission,
            we would welcome a conversation.
          </p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-forest text-white px-8 py-3.5 font-medium hover:bg-forest-dark transition-colors duration-300" data-testid="work-partner-link">
            Talk to us about partnership <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>
    </div>
  );
}
